import * as React from "react";
import dayjs from "dayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { StaticDatePicker } from "@mui/x-date-pickers";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Stack,
  Typography,
  Select,
  MenuItem,
  TextField,
} from "@mui/material";

const toDayjsDate = (d) => {
  if (!d) return dayjs();
  return dayjs.isDayjs(d) ? d : dayjs(d);
};

const toDayjsTime = (dateLike, timeLike) => {
  const base = toDayjsDate(dateLike);
  if (!timeLike) return dayjs();
  const t = dayjs.isDayjs(timeLike) ? timeLike : dayjs(`${base.format("YYYY-MM-DD")}T${timeLike}`);
  return t;
};

const fmtDate = (d) => d.format("YYYY-MM-DD");
const fmtTime = (d) => d.format("HH:mm");

const CustomDateAndTimePicker = ({
  date,
  time,
  onValueChange,
  disablePast = true,
  setDate,
  setTime,
  bgcolor
}) => {
  const now = dayjs();

  // Coerce incoming props to Dayjs for internal use
  const dateDJ = React.useMemo(() => toDayjsDate(date), [date]);
  const timeDJ = React.useMemo(() => toDayjsTime(date, time), [date, time]);

  // Local setters that write STRINGS back up
  const setDateFromDJ = React.useCallback((d) => setDate(fmtDate(d)), [setDate]);
  const setTimeFromDJ = React.useCallback((t) => setTime(fmtTime(t)), [setTime]);

  React.useEffect(() => {
    if (typeof onValueChange === "function") {
      onValueChange({ date: fmtDate(dateDJ), time: fmtTime(timeDJ) });
    }
  }, [dateDJ, timeDJ, onValueChange]);

  const isSameCalendarDay = dateDJ.isSame(now, "day");
  const currentHour24 = timeDJ.hour();

  const handleAmPmChange = (next) => {
    const wasPM = timeDJ.hour() >= 12;
    if (next === "AM" && wasPM) setTimeFromDJ(timeDJ.hour(timeDJ.hour() - 12));
    if (next === "PM" && !wasPM) setTimeFromDJ(timeDJ.hour(timeDJ.hour() + 12));
  };

  const inputStyle = {
    width: "100%",
    height: "",
    border: "none",
    borderRadius: "5px",
    padding: "8px 12px",
    fontSize: "1.5rem",
    color: "rgb(92 92 92)",
    backgroundColor: bgcolor||"#f0f0f0",
    outline: "none",
  };

  const calendarStyle = {
    border: "none",
    borderRadius: "12px",
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
    background: "white",
    fontFamily: "Inter, sans-serif",
    padding: "8px",
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: 2}}>
        <Stack sx={{flex: 1, maxWidth: {xs:'100%',md:'50%'}}}  >
        <div style={{ width: "100%"}}>
            {/* <Typography variant="h6" sx={{ mt: 1, textAlign: "start", opacity: 0.8 }}>
              Set Date
            </Typography> */}
            <DatePicker
              selected={dateDJ.toDate()}
              onChange={(date) => setDateFromDJ(dayjs(date))}
              dateFormat="dd-MM-yyyy"
              className="datepicker-input"
              calendarClassName="custom-calendar"
              popperPlacement="bottom-start"
              customInput={
                <input
                  type="text"
                  value={dayjs(dateDJ).format("DD-MM-YYYY")}
                  style={inputStyle}
                  readOnly
                />
              }
            />

            <Typography variant="caption" color="textSecondary">
              {dateDJ.format("ddd, DD MMM YYYY")} • {timeDJ.format("hh:mm A")}
            </Typography>
        
            <style>
              {`
                /* Calendar container */
                .custom-calendar {
                  border: ${calendarStyle.border} !important;
                  border-radius: ${calendarStyle.borderRadius} !important;
                  box-shadow: ${calendarStyle.boxShadow} !important;
                  background: ${calendarStyle.background} !important;
                  font-family: ${calendarStyle.fontFamily} !important;
                  padding: ${calendarStyle.padding} !important;
                }

                /* Selected day */
                .react-datepicker__day--selected,
                .react-datepicker__day--keyboard-selected {
                  background-color: #000 !important;
                  color: white !important;
                  border-radius: 6px !important;
                }

                /* Hover effect */
                .react-datepicker__day:hover {
                  background-color: #e5e5e5 !important;
                  border-radius: 6px !important;
                }

                /* Month navigation arrows */
                .react-datepicker__navigation-icon::before {
                  border-color: #000 !important;
                }

                /* Header */
                .react-datepicker__current-month {
                  font-weight: 500 !important;
                }
                .react-datepicker-popper{
                  background-color: #fff !important;
                  z-index: 9999 !important;
                }
                .react-datepicker__navigation {
                  position: absolute;
                  top: 17px;
                }
                .react-datepicker__navigation--previous {
                  left: 10px;
                }
                .react-datepicker__navigation--next {
                  right: 10px;
                }
              `}
            </style>
          </div>
        </Stack>
        <Stack sx={{ flex: 1, maxWidth: {xs:'100%',md:'50%'}, alignItems: 'start', justifyContent: 'center' }}>
          {/* <Typography variant="h6" sx={{ mt: 1, textAlign: "start", opacity: 0.8 }}>
            Set Time
          </Typography> */}
          <Stack direction="row" spacing={1} alignItems="center">
            <Stack alignItems="center">
              <TextField
                value={timeDJ.format('hh')}
                onChange={(e) => {
                  const newHour = parseInt(e.target.value, 10);
                  if (!isNaN(newHour) && newHour >= 1 && newHour <= 12) {
                    const currentPeriod = timeDJ.hour() >= 12 ? 'PM' : 'AM';
                    let hour24 = newHour;
                    if (currentPeriod === 'PM' && newHour !== 12) {
                      hour24 += 12;
                    } else if (currentPeriod === 'AM' && newHour === 12) {
                      hour24 = 0;
                    }
                    setTimeFromDJ(timeDJ.hour(hour24));
                  }
                }}
                inputProps={{ type: 'number', min: 1, max: 12 }}
                sx={{
                  width: 80,
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: bgcolor||"#f0f0f0",
                    '& fieldset': { borderColor: 'transparent' },
                    '&:hover fieldset': { borderColor: 'transparent' },
                    '&.Mui-focused fieldset': { borderColor: 'transparent' },
                  },
                  '& .MuiInputBase-input': { textAlign: 'center', fontSize: '1.5rem', padding: '10px' },
                }}
              />
              <Typography variant="caption" color="textSecondary">Hour</Typography>
            </Stack>
            <Typography variant="h5" sx={{ pb: 2 }}>:</Typography>
            <Stack alignItems="center">
              <TextField
                value={timeDJ.format('mm')}
                onChange={(e) => {
                  const newMinute = parseInt(e.target.value, 10);
                  if (!isNaN(newMinute) && newMinute >= 0 && newMinute <= 59) {
                    setTimeFromDJ(timeDJ.minute(newMinute));
                  }
                }}
                inputProps={{ type: 'number', min: 0, max: 59 }}
                sx={{
                  width: 80,
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: bgcolor||"#f0f0f0",
                    '& fieldset': { borderColor: 'transparent' },
                    '&:hover fieldset': { borderColor: 'transparent' },
                    '&.Mui-focused fieldset': { borderColor: 'transparent' },
                  },
                  '& .MuiInputBase-input': { textAlign: 'center', fontSize: '1.5rem', padding: '10px' },
                }}
              />
              <Typography variant="caption" color="textSecondary">Minute</Typography>
            </Stack>
            <Stack sx={{ pb: 2 }}>
              <Box
                onClick={() => handleAmPmChange('AM')}
                sx={{
                  padding: '2px 10px',
                  border: '1px solid #ccc',
                  borderTopLeftRadius: '4px',
                  borderTopRightRadius: '4px',
                  cursor: 'pointer',
                  backgroundColor: timeDJ.hour() < 12 ? '#e1dfff' : bgcolor||"#f0f0f0",
                  color: timeDJ.hour() < 12 ? '#5e35b1' : 'inherit',
                }}
              >
                AM
              </Box>
              <Box
                onClick={() => handleAmPmChange('PM')}
                sx={{
                  padding: '2px 10px',
                  border: '1px solid #ccc',
                  borderTop: 'none',
                  borderBottomLeftRadius: '4px',
                  borderBottomRightRadius: '4px',
                  cursor: 'pointer',
                  backgroundColor: timeDJ.hour() >= 12 ? '#e1dfff' : bgcolor||"#f0f0f0" ,
                  color: timeDJ.hour() >= 12 ? '#5e35b1' : 'inherit',
                }}
              >
                PM
              </Box>
            </Stack>
          </Stack>
      </Stack>
        {/* <Card sx={{ flex: 1, maxWidth: 360 }}>
          <CardHeader
            title={<Typography sx={{ fontWeight: 700 }}>Select Date</Typography>}
            sx={{ pb: 0 }}
          />
          <CardContent>
            <StaticDatePicker
              displayStaticWrapperAs="desktop"
              value={dateDJ}
              onChange={(d) => {
                if (!d) return;
                const next = d.startOf("day");
                setDateFromDJ(next);
                // if disabledPast and same day, clamp time
                if (disablePast && next.isSame(now, "day")) {
                  const tentative = next.hour(timeDJ.hour()).minute(timeDJ.minute());
                  if (tentative.isBefore(now)) setTimeFromDJ(now.startOf("minute"));
                }
              }}
              disablePast={disablePast}
              slotProps={{ actionBar: { actions: [] } }}
            />
          </CardContent>
        </Card>

        <Card sx={{ flex: 1, maxWidth: 360, justifyContent: "center", alignItems: "center" }}>
          <CardHeader
            title={<Typography sx={{ fontWeight: 700 }}>Set Time</Typography>}
            sx={{ pb: 0, mt: 2 }}
          />
          <CardContent>
            <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
              <TextField
                size="small"
                type="time"
                value={fmtTime(timeDJ)}
                onChange={(e) => {
                  const [hh, mm] = e.target.value.split(":").map((x) => parseInt(x, 10));
                  if (Number.isInteger(hh) && Number.isInteger(mm)) {
                    let candidate = timeDJ.hour(hh).minute(mm).second(0).millisecond(0);
                    if (disablePast && isSameCalendarDay) {
                      const tentative = dateDJ.hour(hh).minute(mm);
                      if (tentative.isBefore(now)) candidate = now.startOf("minute");
                    }
                    setTimeFromDJ(candidate);
                  }
                }}
                inputProps={{ step: 60 }}
                sx={{ width: 140 }}
              />

              <Select
                size="small"
                value={currentHour24 >= 12 ? "PM" : "AM"}
                onChange={(e) => handleAmPmChange(e.target.value)}
                sx={{ width: 88 }}
              >
                <MenuItem value="AM">AM</MenuItem>
                <MenuItem value="PM">PM</MenuItem>
              </Select>
            </Stack>

            <Typography variant="body2" sx={{ mt: 1, textAlign: "center", opacity: 0.8 }}>
              {dateDJ.format("ddd, DD MMM YYYY")} • {timeDJ.format("hh:mm A")}
            </Typography>
          </CardContent>
        </Card> */}
      </Box>
    </LocalizationProvider>
  );
};

export default CustomDateAndTimePicker;
